/* BrowsLashes.jsx – Enhanced with advanced filters, search, and sorting (rev 19 Dec 2024) */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaMapMarkerAlt, FaPhone, FaTag, FaHandSparkles } from 'react-icons/fa';
import { http } from '../lib/http';
import { shopImg, SHOP_PLACEHOLDER } from '../utils/img';
import ShopRatingDisplay from './ShopRatingDisplay';
import { useI18n } from '../contexts/I18n';
import ServiceFiltersPanel from './ServiceFiltersPanel';
import './ServiceCommon.css';

const towns = [
  'Filter By Town',
  'Maadi', 'Masr El Gedida', 'Heliopolis', 'Nasr City',
  'New Cairo', 'October City', 'Zamalek', 'Mohandessin',
  'Dokki', 'Giza',
];

/* ⭑ half-star renderer */
const Stars = ({ n = 0 }) => {
  const out = [];
  for (let i = 1; i <= 5; i++) {
    const diff = n - i + 1;
    const cls  = diff >= 1 ? 'filled'
               : diff >= 0.25 ? 'half'
               : '';
    out.push(<span key={i} className={`star ${cls}`}>★</span>);
  }
  return out;
};

export default function BrowsLashes() {
  const { t, lang } = useI18n();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [favorites, setFavorites] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nav = useNavigate();

  // Enhanced filter state
  const [filters, setFilters] = useState({
    town: 'Filter By Town',
    search: '',
    priceRange: [0, 99999],
    rating: 0,
    sortBy: 'popularity',
    sortDirection: 'desc'
  });

  /* fetch once -------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const qs = new URLSearchParams({
          serviceCategory: 'Brows & lashes',
          limit : '1000',
          page  : '1',
        });
        const { data } = await http.getJson(`/businesses?${qs}`);
        setShops(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('load brows & lashes', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Enhanced filtering and sorting logic */
  const filtered = useMemo(() => {
    let result = shops.filter(s => {
      // Town filter
      if (filters.town !== 'Filter By Town' && s.town !== filters.town) return false;
      
      // Search filter
      if (filters.search && !(s.name || '').toLowerCase().includes(filters.search.toLowerCase())) return false;
      
      // Rating filter
      if (filters.rating > 0 && (s.rating || 0) < filters.rating) return false;
      
      // Price range filter
      if (s.services && Array.isArray(s.services)) {
        const avgPrice = s.services.reduce((sum, srv) => sum + (srv.price || 0), 0) / s.services.length;
        if (avgPrice < filters.priceRange[0] || avgPrice > filters.priceRange[1]) return false;
      }
      
      return true;
    });

    // Sorting
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'price':
          const aPrice = a.services?.reduce((sum, s) => sum + (s.price || 0), 0) / a.services?.length || 0;
          const bPrice = b.services?.reduce((sum, s) => sum + (s.price || 0), 0) / b.services?.length || 0;
          comparison = aPrice - bPrice;
          break;
        case 'rating':
          comparison = (b.rating || 0) - (a.rating || 0);
          break;
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'popularity':
        default:
          comparison = (b.rating || 0) * (b.services?.length || 0) - (a.rating || 0) * (a.services?.length || 0);
          break;
      }
      
      return filters.sortDirection === 'desc' ? comparison : -comparison;
    });

    return result;
  }, [shops, filters]);

  /* Search suggestions */
  useEffect(() => {
    if (filters.search.length > 1) {
      const suggestions = shops
        .filter(s => (s.name || '').toLowerCase().includes(filters.search.toLowerCase()))
        .slice(0, 5)
        .map(s => ({
          id: s._id,
          name: s.name,
          type: 'brows-lashes',
          town: s.town
        }));
      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [filters.search, shops]);

  /* Clear all filters */
  const clearFilters = () => {
    setFilters({
      town: 'Filter By Town',
      search: '',
      priceRange: [0, 99999],
      rating: 0,
      sortBy: 'popularity',
      sortDirection: 'desc'
    });
  };

  /* Toggle favorite */
  const toggleFavorite = (shopId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(shopId)) {
        newFavorites.delete(shopId);
      } else {
        newFavorites.add(shopId);
      }
      return newFavorites;
    });
  };

  /* ------------------------------------------------------------------------ */
  return (
    <div className="shops-container" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* ——— header ——— */}
      <header className="header-section">
        <div className="header-left">
          <h1 className="shop-title">{t('allBrowsLashes')}</h1>
          <p className="shop-count">{t('total')}: {filtered.length}</p>
        </div>
      <ServiceFiltersPanel
        filters={filters}
        setFilters={setFilters}
        towns={towns}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />
      </header>

      {/* ——— search with suggestions ——— */}
      <div className="search-section">
      <div className="search-bar-container">
        <FaSearch className="search-icon" aria-hidden="true" />
        <input
          className="search-input"
          type="search"
          placeholder={t('searchBrowsLashes')}
            value={filters.search}
            onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          />
          
          {/* Search Suggestions */}
          {showSuggestions && searchSuggestions.length > 0 && (
            <div className="search-suggestions active">
              {searchSuggestions.map(suggestion => (
                <div
                  key={suggestion.id}
                  className="suggestion-item"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, search: suggestion.name }));
                    setShowSuggestions(false);
                  }}
                >
                  <span className="suggestion-icon"></span>
                  <span className="suggestion-text">{suggestion.name}</span>
                  <span className="suggestion-type">{suggestion.town}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ——— sorting options ——— */}
      <div className="sorting-section">
        <span className="sorting-label">Sort by:</span>
        <div className="sorting-options">
          {[
            { key: 'popularity', label: 'Popularity' },
            { key: 'rating', label: 'Rating' },
            { key: 'price', label: 'Price' },
            { key: 'name', label: 'Name' }
          ].map(option => (
            <button
              key={option.key}
              className={`sort-btn ${filters.sortBy === option.key ? 'active' : ''} ${filters.sortBy === option.key ? filters.sortDirection : ''}`}
              onClick={() => {
                const newDirection = filters.sortBy === option.key && filters.sortDirection === 'desc' ? 'asc' : 'desc';
                setFilters(prev => ({ 
                  ...prev, 
                  sortBy: option.key, 
                  sortDirection: newDirection 
                }));
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* ——— loading state ——— */}
      {loading && (
        <div className="loading-skeleton">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-image"></div>
              <div className="skeleton-text"></div>
              <div className="skeleton-text short"></div>
              <div className="skeleton-text medium"></div>
            </div>
          ))}
        </div>
      )}

      {/* ——— empty state ——— */}
      {!loading && filtered.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <h3 className="empty-state-title">{t('noSalonsFound')}</h3>
          <p className="empty-state-description">
            {t('tryAdjustingFilters')}
          </p>
          <button className="empty-state-action" onClick={clearFilters}>
            {t('clearFilters')}
          </button>
        </div>
      )}

      {/* ——— shop list ——— */}
      {!loading && filtered.length > 0 && (
      <div className="shop-list">
          {filtered.map(shop => {
            const isOpen = !!expanded[shop._id];
            const isFavorite = favorites.has(shop._id);
            const services = Array.isArray(shop.services)
              ? (isOpen ? shop.services : shop.services.slice(0, 3))
              : [];
            const avgPrice = services.length > 0 
              ? services.reduce((sum, s) => sum + (s.price || 0), 0) / services.length 
              : 0;

          return (
            <div
              key={shop._id}
                className={`shop-box${isOpen ? ' expanded' : ''}`}
              onClick={() => nav(`/booking/${encodeURIComponent(shop.name)}`)}
            >
              {/* banner */}
              <img
                className="left-image"
                src={shopImg(shop.imageUrl || (shop.gallery && shop.gallery[0]))}
                alt={shop.name}
                onError={(e) => { e.target.onerror = null; e.target.src = SHOP_PLACEHOLDER; }}
              />

              {/* details */}
              <div className="right-services">
                {/* title line */}
                <div className="shop-info">
                  <h3 className="shop-name">
                    <span className="shop-title-text">{shop.name}</span>
                  </h3>
                  <div className="shop-details">
                    <span className="shop-location">
                      <FaMapMarkerAlt /> {shop.town}
                    </span>
                  </div>
                  <ShopRatingDisplay shop={shop} lang={lang} />
                </div>


                {/* services */}
                {services.map((srv, i) => (
                  <div key={i} className="service-item">
                      <span className="service-name">{srv.serviceName}</span>

                    {/* right-aligned price + book */}
                    <div className="service-price">
                        <span className="price">{srv.price} EGP</span>
                      <button
                        className="book-mini"
                        onClick={e => {
                            e.stopPropagation();
                          nav(`/booking/${encodeURIComponent(shop.name)}`);
                        }}
                      >
                        {t('book')}
                      </button>
                    </div>
                  </div>
                ))}

                {/* toggle + book now */}
                  <div className="shop-card-actions">
                    <button
                        className={`show-more-btn${isOpen ? ' is-open' : ''}`}
                        disabled={!Array.isArray(shop.services) || shop.services.length <= 3}
                        onClick={e => {
                          e.stopPropagation();
                          if (Array.isArray(shop.services) && shop.services.length > 3) {
                            setExpanded(p => ({ ...p, [shop._id]: !p[shop._id] }));
                          }
                        }}
                      >
                        {isOpen ? t('showLess') : t('showMore')}
                      </button>
                    <button
                      className="book-now-btn"
                      onClick={e => {
                        e.stopPropagation();
                        nav(`/booking/${encodeURIComponent(shop.name)}`);
                      }}
                    >
                      {t('recBookNow')}
                    </button>
                  </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
